import { useQuery } from '@tanstack/react-query';
import { createContext, useContext, useEffect, useSyncExternalStore, type ReactNode } from 'react';
import {
  accountService,
  businessService,
  type BusinessContextInfo,
  type MyBusiness,
} from '@/services/api';
import { businessContext } from '@/store/businessContext';

interface BusinessState {
  businesses: MyBusiness[];
  selectedId: string | null;
  context: BusinessContextInfo | null;
  loading: boolean;
  select: (id: string) => void;
}

const Ctx = createContext<BusinessState | null>(null);

export function BusinessProvider({ children }: { children: ReactNode }) {
  const selectedId = useSyncExternalStore(businessContext.subscribe, businessContext.get);
  const mine = useQuery({ queryKey: ['my-businesses'], queryFn: accountService.businesses });
  const businesses = mine.data ?? [];

  // Fall back to the first business when the stored one is gone (membership removed).
  const valid =
    selectedId && businesses.some((b) => b.id === selectedId)
      ? selectedId
      : (businesses[0]?.id ?? null);
  useEffect(() => {
    if (mine.isSuccess && valid !== selectedId) businessContext.set(valid);
  }, [mine.isSuccess, valid, selectedId]);

  const context = useQuery({
    queryKey: ['business-context', valid],
    queryFn: businessService.context,
    enabled: Boolean(valid) && valid === selectedId,
    staleTime: 60_000,
  });

  const value: BusinessState = {
    businesses,
    selectedId: valid,
    context: context.data ?? null,
    loading: mine.isPending || (Boolean(valid) && context.isPending),
    select: (id) => {
      businessContext.set(id);
    },
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useBusiness(): BusinessState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useBusiness must be used inside BusinessProvider');
  return v;
}
